import React, { useState } from "react";
import {
  Modal,
  List,
  Button,
  Tag,
  Typography,
  Card,
  Row,
  Col,
  Statistic,
  Divider,
  message,
} from "antd";
import styled from "styled-components";
import {
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
  HomeOutlined,
  FileTextOutlined,
  BarChartOutlined,
  RiseOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { trackEvent } from "../helpers/mixpanel";
import { getUnitDisplayAddress } from "../lib/unitAddressUtils";
import { exportRentRollToExcel } from "../utils/excelExport";

const { Title, Text } = Typography;

const RentRollReportModal = ({
  isOpen,
  onClose,
  tenants = [],
  units = [],
  totalRentRoll = 0,
  onGenerateStatement,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateStatement = async () => {
    if (onGenerateStatement) {
      onGenerateStatement();
    } else {
      // Default implementation - generate and download PDF
      await generateRentRollStatement();
    }
  };

  const handleExportToExcel = async () => {
    try {
      setIsGenerating(true);

      // Transform tenant data to match Excel export format
      const rentRollData = (tenants || []).map((tenant) => {
        const unit = (units || []).find((u) => u.id === tenant.unitId);
        return {
          propertyName: unit?.propertyName || "N/A",
          unitNumber: unit?.unitNumber || unit?.name || "N/A",
          tenantName: tenant.name || "Unknown",
          tenantEmail: tenant.email || "",
          monthlyRent: tenant.monthlyRent || 0,
          status: tenant.leaseStatus || "Unknown",
          dueDate: tenant.leaseStartDate?.toDate
            ? tenant.leaseStartDate.toDate()
            : null,
          paidDate: null, // TODO: Get from payment data
          amountDue: tenant.monthlyRent || 0,
          amountPaid: 0, // TODO: Get from payment data
          lateFee: 0, // TODO: Get from payment data
          paymentMethod: "", // TODO: Get from payment data
          notes: "",
        };
      });

      const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM format
      await exportRentRollToExcel(rentRollData, currentMonth);

      message.success("Rent roll exported to Excel successfully!");

      trackEvent("rent_roll_excel_exported", {
        tenant_count: (tenants || []).length,
        total_rent: totalRentRoll,
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      message.error("Failed to export to Excel. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const generateRentRollStatement = async () => {
    setIsGenerating(true);
    try {
      // Calculate additional metrics for executive summary
      const safeTenants = tenants || [];
      const safeUnits = units || [];
      const activeTenantsList = safeTenants.filter(
        (t) => t.leaseStatus === "Active"
      );
      const activeTenantsCount = activeTenantsList.length;
      const occupiedUnitsCount = activeTenantsList.filter(
        (t) => t.unitId
      ).length;
      const vacantUnitsCount = safeUnits.length - occupiedUnitsCount;
      const tenantsWithRent = activeTenantsList.filter(
        (t) => t.monthlyRent > 0
      );
      const averageRent =
        tenantsWithRent.length > 0
          ? Math.round(
              tenantsWithRent.reduce((sum, t) => sum + t.monthlyRent, 0) /
                tenantsWithRent.length
            )
          : 0;

      // Calculate annual rent potential
      const annualRentPotential = totalRentRoll * 12;

      // Calculate lease expiration insights
      const now = new Date();
      const leasesExpiringIn30Days = activeTenantsList.filter((t) => {
        if (!t.leaseEndDate) return false;
        const endDate = t.leaseEndDate?.toDate
          ? t.leaseEndDate.toDate()
          : new Date(t.leaseEndDate);
        const daysUntilExpiry = Math.ceil(
          (endDate - now) / (1000 * 60 * 60 * 24)
        );
        return daysUntilExpiry >= 0 && daysUntilExpiry <= 30;
      }).length;

      const leasesExpiringIn90Days = activeTenantsList.filter((t) => {
        if (!t.leaseEndDate) return false;
        const endDate = t.leaseEndDate?.toDate
          ? t.leaseEndDate.toDate()
          : new Date(t.leaseEndDate);
        const daysUntilExpiry = Math.ceil(
          (endDate - now) / (1000 * 60 * 60 * 24)
        );
        return daysUntilExpiry >= 0 && daysUntilExpiry <= 90;
      }).length;

      // Calculate rent range
      const rents = tenantsWithRent.map((t) => t.monthlyRent);
      const minRent = rents.length > 0 ? Math.min(...rents) : 0;
      const maxRent = rents.length > 0 ? Math.max(...rents) : 0;

      // Calculate occupancy rate
      const occupancyRate =
        safeUnits.length > 0
          ? Math.round((occupiedUnitsCount / safeUnits.length) * 100)
          : 0;

      // Create a comprehensive portfolio yield statement
      const statementData = {
        title: "Portfolio Yield Report",
        generatedDate: new Date().toLocaleDateString(),
        totalRentRoll: totalRentRoll,
        activeTenants: activeTenantsCount,
        totalUnits: units.length,
        occupiedUnits: occupiedUnitsCount,
        vacantUnits: vacantUnitsCount,
        averageRent: averageRent,
        annualRentPotential: annualRentPotential,
        occupancyRate: occupancyRate,
        minRent: minRent,
        maxRent: maxRent,
        leasesExpiringIn30Days: leasesExpiringIn30Days,
        leasesExpiringIn90Days: leasesExpiringIn90Days,
        tenants: (tenants || []).map((tenant) => {
          const unit = (units || []).find((u) => u.id === tenant.unitId);
          return {
            name: tenant.name || "Unknown Tenant",
            email: tenant.email || "No email",
            phone: tenant.phone || "No phone",
            unitName: unit
              ? unit.name || getUnitDisplayAddress(unit)
              : "No door assigned",
            monthlyRent: tenant.monthlyRent || 0,
            leaseStatus: tenant.leaseStatus || "Unknown",
            leaseStartDate:
              tenant.leaseStartDate?.toDate?.()?.toLocaleDateString() ||
              "Unknown",
            leaseEndDate:
              tenant.leaseEndDate?.toDate?.()?.toLocaleDateString() ||
              "Month-to-month",
          };
        }),
      };

      // Generate PDF content
      const pdfContent = generatePDFContent(statementData);

      // Create and download the PDF
      await downloadPDF(
        pdfContent,
        `rent-roll-report-${new Date().toISOString().split("T")[0]}.pdf`
      );

      // Track the event
      try {
        trackEvent("rent_roll_statement_generated", {
          totalRentRoll: totalRentRoll,
          activeTenants: statementData.activeTenants,
          totalUnits: statementData.totalUnits,
          statementType: "rent_roll_report",
        });
      } catch (error) {
        console.warn(
          "Failed to track portfolio yield statement generation:",
          error
        );
      }
    } catch (error) {
      console.error("Error generating portfolio yield statement:", error);
      message.error(
        "Failed to generate portfolio yield statement. Please try again."
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const generatePDFContent = (data) => {
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${data.title}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Times New Roman', Times, serif;
            margin: 0;
            padding: 0;
            color: #000000;
            line-height: 1.6;
            background: #ffffff;
            font-size: 11pt;
          }
          .report-container {
            max-width: 8.5in;
            margin: 0 auto;
            padding: 0.75in;
          }
          .report-header {
            border-bottom: 3px solid #2c5f2d;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .report-title {
            font-size: 24pt;
            font-weight: bold;
            color: #2c5f2d;
            margin-bottom: 10px;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .report-meta {
            font-size: 10pt;
            color: #666666;
            margin-top: 8px;
          }
          .executive-summary {
            margin-bottom: 35px;
            page-break-inside: avoid;
          }
          .section-title {
            font-size: 14pt;
            font-weight: bold;
            color: #2c5f2d;
            margin-bottom: 15px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            border-bottom: 2px solid #2c5f2d;
            padding-bottom: 5px;
          }
          .summary-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
            page-break-inside: avoid;
          }
          .summary-table td {
            padding: 12px 15px;
            border: 1px solid #cccccc;
            font-size: 10pt;
          }
          .summary-table td:first-child {
            font-weight: bold;
            background-color: #f5f5f5;
            width: 40%;
            color: #333333;
          }
          .summary-table td:last-child {
            text-align: right;
            font-weight: bold;
            color: #2c5f2d;
            font-size: 11pt;
          }
          .tenants-section {
            margin-top: 120px;
            padding-top: 60px;
            page-break-inside: avoid;
            page-break-before: always;
          }
          .tenants-section .section-title {
            margin-top: 0;
            margin-bottom: 20px;
          }
          .tenants-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 15px;
            font-size: 9pt;
            table-layout: fixed;
            word-wrap: break-word;
            border-spacing: 0;
          }
          .tenants-table thead {
            background-color: #ffffff;
            border-bottom: 2px solid #2c5f2d;
          }
          .tenants-table th {
            padding: 10px 8px;
            text-align: left;
            font-weight: bold;
            font-size: 8pt;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #2c5f2d;
            border: 1px solid #2c5f2d;
            background-color: #ffffff;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .tenants-table th:nth-child(1) { width: 18%; } /* Tenant Name */
          .tenants-table th:nth-child(2) { width: 20%; } /* Email */
          .tenants-table th:nth-child(3) { width: 11%; } /* Phone */
          .tenants-table th:nth-child(4) { width: 8%; }  /* Unit */
          .tenants-table th:nth-child(5) { width: 11%; } /* Lease Start */
          .tenants-table th:nth-child(6) { width: 11%; } /* Lease End */
          .tenants-table th:nth-child(7) { width: 10%; } /* Status */
          .tenants-table th:nth-child(8) { width: 10%; } /* Monthly Rent */
          .tenants-table th:nth-child(5),
          .tenants-table th:nth-child(6),
          .tenants-table th:nth-child(7) {
            text-align: center;
          }
          .tenants-table th:last-child {
            text-align: right;
          }
          .tenants-table td {
            padding: 8px 8px;
            border: 1px solid #cccccc;
            vertical-align: top;
            font-size: 9pt;
            white-space: normal;
            word-wrap: break-word;
            overflow-wrap: break-word;
            line-height: 1.4;
            overflow-x: hidden;
            overflow-y: visible;
            min-height: 1.4em;
          }
          .tenants-table td:nth-child(1) {
            /* Tenant Name - allow 2 lines */
            max-height: 2.8em;
            overflow-y: hidden;
          }
          .tenants-table td:nth-child(2) {
            /* Email - allow wrapping */
            word-break: break-all;
          }
          .tenants-table td:nth-child(3) {
            /* Phone - allow wrapping */
            font-size: 8pt;
            word-break: break-word;
          }
          .tenants-table td:nth-child(4) {
            /* Unit - allow wrapping */
          }
          .tenants-table td:nth-child(5),
          .tenants-table td:nth-child(6) {
            /* Lease dates - prevent wrapping */
            font-size: 8pt;
            white-space: nowrap;
            overflow-x: hidden;
            overflow-y: visible;
          }
          .tenants-table td:nth-child(7) {
            /* Status - allow wrapping */
            font-size: 8pt;
          }
          .tenants-table td:nth-child(8) {
            /* Monthly Rent - keep on one line */
            white-space: nowrap;
            overflow-x: hidden;
            overflow-y: visible;
          }
          .tenants-table tbody tr {
            height: auto;
            min-height: 2em;
          }
          .tenants-table tbody tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          .tenants-table tbody tr:hover {
            background-color: #f0f0f0;
          }
          .tenants-table td:last-child {
            text-align: right;
            font-weight: bold;
            color: #2c5f2d;
            white-space: nowrap;
          }
          .tenants-table td:nth-child(5),
          .tenants-table td:nth-child(6),
          .tenants-table td:nth-child(7) {
            text-align: center;
            font-size: 8pt;
          }
          .status-active {
            color: #155724;
            font-weight: bold;
            font-size: 8pt;
          }
          .status-pending {
            color: #856404;
            font-weight: bold;
            font-size: 8pt;
          }
          .status-inactive {
            color: #721c24;
            font-weight: bold;
            font-size: 8pt;
          }
          .status-unknown {
            background-color: #262626;
            color: #ffffff;
            font-weight: bold;
            font-size: 8pt;
            padding: 2px 6px;
            border-radius: 3px;
          }
          .footer-section {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #cccccc;
            font-size: 9pt;
            color: #666666;
            text-align: center;
            page-break-inside: avoid;
          }
          .disclaimer {
            margin-top: 30px;
            padding: 15px;
            background-color: #f8f9fa;
            border-left: 4px solid #2c5f2d;
            font-size: 9pt;
            color: #333333;
            line-height: 1.5;
            page-break-inside: avoid;
          }
          .disclaimer-title {
            font-weight: bold;
            margin-bottom: 5px;
            color: #2c5f2d;
          }
          .page-break {
            page-break-before: always;
          }
          @media print {
            .report-container {
              padding: 0.5in;
            }
            .tenants-table tbody tr {
              page-break-inside: avoid;
            }
            .tenants-section {
              page-break-after: avoid;
            }
            .disclaimer {
              page-break-before: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="report-container">
          <div class="report-header">
            <div class="report-title">${data.title}</div>
            <div class="report-meta">
              <div>Report Date: ${data.generatedDate}</div>
              <div>Generated by: Leo AI Assistant - LandlordOps</div>
            </div>
          </div>

          <div class="executive-summary">
            <div class="section-title">Executive Summary</div>
            
            <div style="margin-bottom: 25px;">
              <div style="font-size: 11pt; font-weight: bold; color: #2c5f2d; margin-bottom: 15px; text-transform: uppercase; letter-spacing: 0.5px;">Portfolio Overview</div>
              <table class="summary-table">
                <tr>
                  <td>Total Doors</td>
                  <td>${data.totalUnits}</td>
                </tr>
                <tr>
                  <td>Occupied Doors</td>
                  <td>${data.occupiedUnits}</td>
                </tr>
                <tr>
                  <td>Vacant Doors</td>
                  <td>${data.vacantUnits}</td>
                </tr>
                <tr>
                  <td>Occupancy Rate</td>
                  <td>${data.occupancyRate}%</td>
                </tr>
                <tr>
                  <td>Active Tenants</td>
                  <td>${data.activeTenants}</td>
                </tr>
              </table>
            </div>

            <div style="margin-bottom: 25px;">
              <div style="font-size: 11pt; font-weight: bold; color: #2c5f2d; margin-bottom: 15px; text-transform: uppercase; letter-spacing: 0.5px;">Financial Metrics</div>
              <table class="summary-table">
                <tr>
                  <td>Total Monthly Portfolio Yield</td>
                  <td>$${data.totalRentRoll.toLocaleString()}</td>
                </tr>
                <tr>
                  <td>Annual Rent Potential</td>
                  <td>$${data.annualRentPotential.toLocaleString()}</td>
                </tr>
                <tr>
                  <td>Average Monthly Rent</td>
                  <td>$${data.averageRent.toLocaleString()}</td>
                </tr>
                <tr>
                  <td>Rent Range (Low - High)</td>
                  <td>$${data.minRent.toLocaleString()} - $${data.maxRent.toLocaleString()}</td>
                </tr>
              </table>
            </div>

            <div>
              <div style="font-size: 11pt; font-weight: bold; color: #2c5f2d; margin-bottom: 15px; text-transform: uppercase; letter-spacing: 0.5px;">Lease Management</div>
              <table class="summary-table">
                <tr>
                  <td>Leases Expiring in Next 30 Days</td>
                  <td>${data.leasesExpiringIn30Days}</td>
                </tr>
                <tr>
                  <td>Leases Expiring in Next 90 Days</td>
                  <td>${data.leasesExpiringIn90Days}</td>
                </tr>
                <tr>
                  <td>Lease Renewal Rate (Est.)</td>
                  <td>${
                    data.activeTenants > 0
                      ? Math.round(
                          ((data.activeTenants - data.leasesExpiringIn90Days) /
                            data.activeTenants) *
                            100
                        )
                      : 100
                  }%</td>
                </tr>
              </table>
            </div>
          </div>

          <div class="tenants-section">
            <div class="section-title">Tenant Roster</div>
            <table class="tenants-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Unit</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Status</th>
                  <th>Rent</th>
                </tr>
              </thead>
              <tbody>
                ${(data.tenants || [])
                  .map(
                    (tenant) => `
                  <tr>
                    <td><strong>${tenant.name}</strong></td>
                    <td>${tenant.email}</td>
                    <td>${tenant.phone}</td>
                    <td>${tenant.unitName}</td>
                    <td>${tenant.leaseStartDate}</td>
                    <td>${tenant.leaseEndDate}</td>
                    <td><span class="status-${String(
                      tenant.leaseStatus || "unknown"
                    ).toLowerCase()}">${
                      tenant.leaseStatus || "Unknown"
                    }</span></td>
                    <td>$${tenant.monthlyRent.toLocaleString()}</td>
                  </tr>
                `
                  )
                  .join("")}
              </tbody>
            </table>
          </div>

          <div class="disclaimer">
            <div class="disclaimer-title">Report Information</div>
            <div>
              This portfolio yield report provides a comprehensive overview of current rental income and tenant occupancy as of ${
                data.generatedDate
              }. 
              All amounts listed represent monthly rental rates. This document may be used for financial planning, property valuation, 
              and investment analysis purposes. For questions regarding this report, please contact your property management team.
            </div>
          </div>

          <div class="footer-section">
            <div>This document was generated by LandlordOps</div>
            <div style="margin-top: 5px;">© ${new Date().getFullYear()} LandlordOps. All rights reserved.</div>
          </div>
        </div>
      </body>
      </html>
    `;

    return htmlContent;
  };

  const downloadPDF = async (htmlContent, filename) => {
    try {
      // Create a temporary container element with proper styling
      const tempDiv = document.createElement("div");
      tempDiv.style.position = "absolute";
      tempDiv.style.left = "-9999px";
      tempDiv.style.top = "0";
      tempDiv.style.width = "210mm"; // A4 width
      tempDiv.style.backgroundColor = "#ffffff";
      tempDiv.style.padding = "0";
      tempDiv.style.margin = "0";
      tempDiv.innerHTML = htmlContent;
      document.body.appendChild(tempDiv);

      // Wait a bit for styles to apply
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Wait for images to load
      await new Promise((resolve) => {
        const images = tempDiv.getElementsByTagName("img");
        let loadedImages = 0;
        const totalImages = images.length;

        if (totalImages === 0) {
          resolve();
          return;
        }

        const imageLoaded = () => {
          loadedImages++;
          if (loadedImages === totalImages) {
            resolve();
          }
        };

        Array.from(images).forEach((img) => {
          if (img.complete) {
            imageLoaded();
          } else {
            img.onload = imageLoaded;
            img.onerror = imageLoaded;
          }
        });
      });

      // Convert HTML to canvas with optimized settings for smaller file size
      const canvas = await html2canvas(tempDiv, {
        scale: 1.5, // Reduced from 2 to 1.5 for smaller file size (still good quality)
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        width: tempDiv.scrollWidth,
        height: tempDiv.scrollHeight,
        windowWidth: tempDiv.scrollWidth,
        windowHeight: tempDiv.scrollHeight,
        allowTaint: false,
        foreignObjectRendering: false,
      });

      // Remove temporary element
      document.body.removeChild(tempDiv);

      // Calculate PDF dimensions
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pdf = new jsPDF("p", "mm", "a4");

      // Add pages if content is taller than one page
      const pageHeight = 297; // A4 height in mm
      const pageWidth = 210; // A4 width in mm
      let heightLeft = imgHeight;
      let position = 0;

      // Use JPEG format with compression for much smaller file size
      // Quality 0.85 provides good balance between quality and file size
      const imageData = canvas.toDataURL("image/jpeg", 0.85);
      // Add first page
      pdf.addImage(imageData, "JPEG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imageData, "JPEG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Download the PDF
      pdf.save(filename);

      // Show success message
      message.success(
        "Rent roll report generated and downloaded successfully!"
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.error("Failed to generate PDF. Please try again.");
      throw error;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "pending":
        return "warning";
      case "inactive":
        return "error";
      case "evicted":
        return "error";
      default:
        return "default";
    }
  };

  const isUnknownLeaseStatus = (status) =>
    status == null ||
    String(status).trim() === "" ||
    String(status).toLowerCase() === "unknown";

  const activeTenants = tenants.filter((t) => t.leaseStatus === "Active");
  const occupiedUnits = activeTenants.filter((t) => t.unitId).length;
  const vacantUnits = units.length - occupiedUnits;
  const averageRent =
    activeTenants.filter((t) => t.monthlyRent > 0).length > 0
      ? Math.round(
          activeTenants
            .filter((t) => t.monthlyRent > 0)
            .reduce((sum, t) => sum + t.monthlyRent, 0) /
            activeTenants.filter((t) => t.monthlyRent > 0).length
        )
      : 0;

  return (
    <StyledModal
      title={
        <ModalHeader>
          <BarChartOutlined
            style={{
              color: "white",
              marginRight: "8px",
              filter: "brightness(0) invert(1)",
            }}
          />
          Portfolio Yield Report
        </ModalHeader>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width="90%"
      centered
    >
      <ModalContent>
        {/* Summary Statistics */}
        <SummarySection>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Total Portfolio Yield"
                value={totalRentRoll}
                prefix={<DollarOutlined />}
                valueStyle={{ color: "#2c5f2d" }}
                formatter={(value) => `$${value.toLocaleString()}`}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Active Tenants"
                value={activeTenants.length}
                prefix={<TeamOutlined />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Total Doors"
                value={units.length}
                prefix={<HomeOutlined />}
                valueStyle={{ color: "#722ed1" }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Average Rent"
                value={averageRent}
                prefix={<RiseOutlined />}
                valueStyle={{ color: "#fa8c16" }}
                formatter={(value) => `$${value.toLocaleString()}`}
              />
            </Col>
          </Row>
        </SummarySection>

        <Divider />

        {/* Action Items */}
        <ActionSection>
          <Title level={4}>Report Actions</Title>
          <ActionGrid>
            <ActionCard>
              <div style={{ display: "flex", gap: "14px", width: "100%" }}>
                <ActionIcon>
                  <FileTextOutlined />
                </ActionIcon>
                <ActionContent>
                  <ActionText>
                    <ActionTitle>Generate PDF Report</ActionTitle>
                    <ActionDescription>
                      Create a detailed portfolio yield statement for financial
                      planning
                    </ActionDescription>
                  </ActionText>
                  <ActionButton
                    type="primary"
                    onClick={handleGenerateStatement}
                    loading={isGenerating}
                    disabled={isGenerating}
                  >
                    {isGenerating ? "Generating..." : "Generate PDF"}
                  </ActionButton>
                </ActionContent>
              </div>
            </ActionCard>
            <ActionCard>
              <div style={{ display: "flex", gap: "14px", width: "100%" }}>
                <ActionIcon>
                  <FileTextOutlined />
                </ActionIcon>
                <ActionContent>
                  <ActionText>
                    <ActionTitle>Export to Excel (QuickBooks)</ActionTitle>
                    <ActionDescription>
                      Download as Excel spreadsheet optimized for QuickBooks
                      import
                    </ActionDescription>
                  </ActionText>
                  <ActionButton
                    type="default"
                    onClick={handleExportToExcel}
                    loading={isGenerating}
                    disabled={isGenerating}
                    style={{ borderColor: "#2c5f2d", color: "#2c5f2d" }}
                  >
                    Export Excel
                  </ActionButton>
                </ActionContent>
              </div>
            </ActionCard>
          </ActionGrid>
        </ActionSection>

        <Divider />

        {/* Tenant Details */}
        <TenantsSection>
          <Title level={4}>Tenant Details</Title>
          {(tenants || []).length === 0 ? (
            <EmptyState>
              <Text type="secondary">
                No tenants found. Add tenants to see portfolio yield details.
              </Text>
            </EmptyState>
          ) : (
            <List
              dataSource={tenants || []}
              renderItem={(tenant) => {
                const unit = (units || []).find((u) => u.id === tenant.unitId);

                return (
                  <TenantItem>
                    <TenantInfo>
                      <TenantHeader>
                        <TenantName>
                          {tenant.name || "Unknown Tenant"}
                        </TenantName>
                        <TenantRent>
                          ${tenant.monthlyRent?.toLocaleString() || 0}
                        </TenantRent>
                        <Tag
                          color={
                            isUnknownLeaseStatus(tenant.leaseStatus)
                              ? undefined
                              : getStatusColor(tenant.leaseStatus)
                          }
                          style={{
                            fontWeight: 600,
                            borderRadius: "6px",
                            fontSize: "12px",
                            ...(isUnknownLeaseStatus(tenant.leaseStatus)
                              ? {
                                  backgroundColor: "#262626",
                                  color: "#ffffff",
                                  borderColor: "#434343",
                                }
                              : {}),
                          }}
                        >
                          {tenant.leaseStatus || "Unknown"}
                        </Tag>
                      </TenantHeader>
                      <TenantDetails>
                        <TenantDetail>
                          <UserOutlined />
                          <Text>{tenant.email || "No email"}</Text>
                        </TenantDetail>
                        <TenantDetail>
                          <CalendarOutlined />
                          <Text>{tenant.phone || "No phone"}</Text>
                        </TenantDetail>
                        <TenantDetail>
                          <HomeOutlined />
                          <Text>
                            {unit
                              ? unit.name || getUnitDisplayAddress(unit)
                              : "No door assigned"}
                          </Text>
                        </TenantDetail>
                        <TenantDetail>
                          <CalendarOutlined />
                          <Text>
                            Lease:{" "}
                            {tenant.leaseStartDate
                              ?.toDate?.()
                              ?.toLocaleDateString() || "Unknown"}{" "}
                            -{" "}
                            {tenant.leaseEndDate
                              ?.toDate?.()
                              ?.toLocaleDateString() || "Month-to-month"}
                          </Text>
                        </TenantDetail>
                      </TenantDetails>
                    </TenantInfo>
                  </TenantItem>
                );
              }}
            />
          )}
        </TenantsSection>
      </ModalContent>
    </StyledModal>
  );
};

// Styled Components
const StyledModal = styled(Modal)`
  --yield-header-height: 56px;
  --yield-close-size: 32px;
  --yield-close-top: 12px;

  .ant-modal-header {
    background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 16px 20px;
    min-height: var(--yield-header-height);
    box-sizing: border-box;
    display: flex;
    align-items: center;
    position: relative;
  }

  .ant-modal-title {
    color: white !important;
    font-weight: 600;
    font-size: 18px;
    line-height: 1.2;
  }

  .ant-modal-close {
    color: white !important;
    font-size: 16px;
    top: var(--yield-close-top) !important;
    right: 20px;
    background: transparent !important;
    border-radius: 0 !important;
    width: var(--yield-close-size) !important;
    height: var(--yield-close-size) !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border: none !important;
    transition: background 0.2s, border-color 0.2s !important;

    &:hover {
      color: white !important;
      background: transparent !important;
      border-color: transparent !important;
    }
  }

  .ant-modal-close .ant-modal-close-x,
  .ant-modal-close .ant-modal-close-icon,
  .ant-modal-close .anticon,
  .ant-modal-close svg,
  .ant-modal-close:hover .ant-modal-close-x,
  .ant-modal-close:hover .ant-modal-close-icon,
  .ant-modal-close:hover .anticon,
  .ant-modal-close:hover svg {
    color: #ffffff !important;
    fill: #ffffff !important;
  }

  .ant-modal-body {
    padding: 24px;
    background: #fafafa;
    overflow-x: hidden;
  }

  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    max-width: 100vw;
    padding: 0px;
  }

  .ant-modal {
    margin: 20px auto;
  }

  /* Desktop/Tablet: 25% less width */
  @media (min-width: 769px) {
    .ant-modal-wrap .ant-modal {
      width: 67.5% !important;
      max-width: 67.5vw !important;
    }
    .ant-modal-content {
      width: 52rem !important;
      max-width: 100% !important;
      margin: auto;
    }
  }

  /* Mobile optimizations */
  @media (max-width: 768px) {
    --yield-header-height: 48px;
    --yield-close-size: 28px;
    --yield-close-top: 10px;

    .ant-modal {
      max-width: 95vw;
      margin: 10px auto;
    }

    .ant-modal-body {
      padding: 16px;
    }

    .ant-modal-header {
      padding: 12px 16px;
    }

    .ant-modal-title {
      font-size: 16px;
    }

    .ant-modal-close {
      right: 16px !important;
      font-size: 14px;
      position: absolute !important;
    }
  }

  @media (max-width: 480px) {
    --yield-header-height: 44px;
    --yield-close-size: 26px;
    --yield-close-top: 9px;

    .ant-modal {
      max-width: 98vw;
      margin: 5px auto;
    }

    .ant-modal-body {
      padding: 12px;
    }

    .ant-modal-header {
      padding: 10px 12px;
    }

    .ant-modal-title {
      font-size: 15px;
    }

    .ant-modal-close {
      right: 12px !important;
      font-size: 12px;
      position: absolute !important;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  color: white;
  line-height: 1.2;

  .anticon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  @media (max-width: 768px) {
    align-items: center;
  }
`;

const ModalContent = styled.div`
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;
  background: white;
  border-radius: 12px;
  padding: 20px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
    max-height: 75vh;
  }

  @media (max-width: 480px) {
    padding: 12px;
    max-height: 80vh;
  }
`;

const SummarySection = styled.div`
  --summary-number-size: 20px;
  margin-bottom: 24px;
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .ant-statistic {
    text-align: center;
  }

  .ant-statistic-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--color-text-light);
    margin-bottom: 6px;
  }

  .ant-statistic-content,
  .ant-statistic-content-value,
  .ant-statistic-content-value-int,
  .ant-statistic-content-value-decimal {
    font-size: var(--summary-number-size) !important;
    font-weight: 700 !important;
    color: var(--color-text) !important;
    line-height: 1.2 !important;
  }

  .ant-statistic-content-prefix {
    font-size: var(--summary-number-size) !important;
    font-weight: 700 !important;
    color: var(--color-text) !important;
    margin-right: 6px;
  }

  .ant-statistic-content-suffix {
    font-size: var(--summary-number-size) !important;
    font-weight: 700 !important;
    color: var(--color-text) !important;
    margin-left: 3px;
  }

  @media (max-width: 768px) {
    --summary-number-size: 18px;
    margin-bottom: 20px;
    padding: 16px;

    .ant-statistic-title {
      font-size: 12px;
    }

  }

  @media (max-width: 480px) {
    --summary-number-size: 16px;
    margin-bottom: 16px;
    padding: 14px;

    .ant-statistic-title {
      font-size: 11px;
    }

  }
`;

const ActionSection = styled.div`
  margin-bottom: 24px;

  h4 {
    font-size: 18px;
    font-weight: 600;
    color: var(--color-text);
    margin-bottom: 16px;
  }

  @media (max-width: 768px) {
    margin-bottom: 20px;

    h4 {
      font-size: 16px;
      margin-bottom: 12px;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 16px;

    h4 {
      font-size: 15px;
      margin-bottom: 10px;
    }
  }
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
  margin-top: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-top: 12px;
  }

  @media (max-width: 480px) {
    gap: 10px;
    margin-top: 10px;
  }
`;

const ActionCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
  padding: 16px;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-primary);
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.15);
  }

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    padding: 14px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    padding: 12px;
    gap: 10px;
    flex-direction: column;
    text-align: center;
  }
`;

const ActionIcon = styled.div`
  width: 40px;
  height: 40px;
  color: #1e293b;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  background: none;
  box-shadow: none;
  padding-top: 2px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ActionContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
    text-align: center;
    align-items: center;
  }
`;

const ActionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  margin-top: 2px;

  @media (max-width: 768px) {
    margin-top: 0;
  }

  @media (max-width: 480px) {
    align-items: center;
  }
`;

const ActionTitle = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: var(--color-text);
  margin-bottom: 4px;

  @media (max-width: 768px) {
    font-size: 14px;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    margin-bottom: 3px;
  }
`;

const ActionDescription = styled.div`
  font-size: 13px;
  color: var(--color-text-light);
  margin-bottom: 10px;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 12px;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    margin-bottom: 6px;
  }
`;

const ActionButton = styled(Button)`
  margin-top: auto;
  margin-left: 0;
  align-self: stretch;
  width: 100%;
  border-radius: 6px;
  font-weight: 600;
  height: 32px;
  padding: 0 12px;
  font-size: 13px;

  &.ant-btn-primary {
    background: var(--color-primary);
    border-color: var(--color-primary);

    &:hover {
      background: #4a7c59;
      border-color: #4a7c59;
    }
  }

  &.ant-btn-default {
    border-color: #d9d9d9;
    color: var(--color-text);

    &:hover {
      border-color: var(--color-primary);
      color: var(--color-primary);
    }
  }

  @media (max-width: 768px) {
    margin-left: auto;
    margin-top: 0;
    align-self: auto;
    width: auto;
    height: 30px;
    padding: 0 10px;
    font-size: 12px;
  }

  @media (max-width: 480px) {
    height: 28px;
    padding: 0 8px;
    font-size: 11px;
    width: 100%;
    margin-top: 4px;
    margin-left: 0;
    align-self: stretch;
  }
`;

const TenantsSection = styled.div`
  margin-bottom: 24px;

  h4 {
    font-size: 18px;
    font-weight: 600;
    color: var(--color-text);
    margin-bottom: 16px;
  }

  @media (max-width: 768px) {
    margin-bottom: 20px;

    h4 {
      font-size: 16px;
      margin-bottom: 12px;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 16px;

    h4 {
      font-size: 15px;
      margin-bottom: 10px;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #999;

  @media (max-width: 768px) {
    padding: 30px 15px;
  }

  @media (max-width: 480px) {
    padding: 20px 10px;
    font-size: 14px;
  }
`;

const TenantItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  margin-bottom: 12px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-primary);
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.15);
  }

  @media (max-width: 768px) {
    padding: 14px;
    margin-bottom: 10px;
  }

  @media (max-width: 480px) {
    padding: 12px;
    margin-bottom: 8px;
    flex-direction: column;
    gap: 10px;
  }
`;

const TenantInfo = styled.div`
  flex: 1;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const TenantHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    gap: 8px;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    gap: 6px;
    margin-bottom: 6px;
    flex-wrap: wrap;
  }
`;

const TenantName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #2c5f2d;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 15px;
  }
`;

const TenantRent = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #2c5f2d;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 15px;
  }
`;

const TenantDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px 24px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const TenantDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 0 1 auto;
  font-size: 13px;
  color: var(--color-text-light);

  .anticon {
    color: var(--color-text-light);
    font-size: 13px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    font-size: 12px;

    .anticon {
      font-size: 12px;
    }
  }

  @media (max-width: 480px) {
    font-size: 11px;

    .anticon {
      font-size: 11px;
    }
  }
`;

export default RentRollReportModal;
